import { BaseFeature } from '../../core/base-feature.js';

/**
 * JigsawFeature - Frontend feature for the Jigsaw Lockout game
 *
 * Sends an image to the desktop, which is locked behind a fullscreen jigsaw of
 * that image. Streams live assembly progress back, and lets the operator
 * inflict "unfair tricks" to make the puzzle harder or impossible.
 */
export class JigsawFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'jigsaw',
            name: 'Jigsaw Lockout',
            version: 'v1.0',
            tier: 'external',
            capabilities: ['start', 'trick', 'get_state', 'end'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Jigsaw] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);
                break;

            case 'start_response':
            case 'trick_response':
            case 'get_state_response':
            case 'end_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Jigsaw] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 15000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => { clearTimeout(timeout); resolve(result); },
                reject
            });
        });
    }

    // ==================== Game Actions ====================

    /**
     * @param {Object} config
     * @param {string} config.imageDataUrl - full data: URL of the image
     * @param {string} [config.difficulty] - easy|medium|hard|brutal
     * @param {string[]} [config.initialTricks] - toggle tricks armed at launch
     */
    startGame(config = {}) {
        return this._sendRequest('start', config);
    }

    /**
     * Inflict a trick.
     * @param {string} type
     * @param {Object} [params] - e.g. { enabled: true } for toggles, { count: 3 }
     */
    trick(type, params = {}) {
        return this._sendRequest('trick', { type, params });
    }

    getState() {
        return this._sendRequest('get_state');
    }

    endGame() {
        return this._sendRequest('end');
    }

    // ==================== Subscriptions ====================

    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try { callback(data); } catch (error) { console.error('[Jigsaw] state callback error:', error); }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
